import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../features/auth/services/api';
import { PLATFORM_NAME } from '../config/brand';
import '../styles/Auth.css';

const OTP_LENGTH = 6;

const ForgotPasswordPage = () => {
  // Step management: 'email' → 'otp' → 'new-password' → 'success'
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const otpRefs = useRef([]);
  const timerRef = useRef(null);

  const startCooldown = useCallback(() => {
    setCooldown(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Auto-focus OTP input
  useEffect(() => {
    if (step === 'otp' && otpRefs.current[0]) {
      setTimeout(() => otpRefs.current[0]?.focus(), 200);
    }
  }, [step]);

  // ── Step 1: Send OTP ──────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await authAPI.forgotPassword(email);
      setStep('otp');
      startCooldown();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  // ── OTP handlers ──────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
    if (value && index === OTP_LENGTH - 1 && newOtp.every(d => d !== '')) {
      handleVerifyOtp(newOtp.join(''));
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted.length) return;
    const newOtp = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    const fi = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[fi]?.focus();
    if (pasted.length === OTP_LENGTH) handleVerifyOtp(pasted);
  };

  // ── Step 2: Verify OTP ────────────────────────────────────────
  const handleVerifyOtp = async (code) => {
    const otpCode = code || otp.join('');
    if (otpCode.length !== OTP_LENGTH) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await authAPI.verifyResetOtp(email, otpCode);
      setResetToken(response.data.reset_token);
      setStep('new-password');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code. Please try again.');
      setOtp(Array(OTP_LENGTH).fill(''));
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
    setIsLoading(false);
  };

  // ── Resend OTP ────────────────────────────────────────────────
  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    try {
      await authAPI.forgotPassword(email);
      setResendMessage('A new code has been sent.');
      setOtp(Array(OTP_LENGTH).fill(''));
      startCooldown();
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    }
    setResending(false);
  };

  // ── Step 3: Set new password ──────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain at least one letter and one number');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await authAPI.resetPassword(resetToken, password);
      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    }
    setIsLoading(false);
  };

  // ── RENDER: Success ───────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="verification-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="#38a169" opacity="0.1"/>
              <path d="M20 32l8 8 16-16" stroke="#38a169" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 style={{ fontSize: '24px', color: '#2d3748', marginBottom: '8px' }}>
            Password Reset!
          </h1>
          <p style={{ color: '#718096', marginBottom: '24px' }}>
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <Link to="/login" className="auth-button" style={{ display: 'inline-flex', textDecoration: 'none' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // ── RENDER: New Password ──────────────────────────────────────
  if (step === 'new-password') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="verification-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#38a169" opacity="0.1"/>
                <path d="M20 32l8 8 16-16" stroke="#38a169" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Set New Password</h1>
            <p>Create a strong password for your account</p>
          </div>

          <form onSubmit={handleResetPassword} className="auth-form">
            {error && (
              <div className="error-message">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="currentColor"/>
                </svg>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Min 6 chars, 1 letter + 1 number"
                required
                autoComplete="new-password"
                minLength="6"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                placeholder="Confirm your new password"
                required
                autoComplete="new-password"
              />
            </div>

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Resetting…
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── RENDER: OTP input ─────────────────────────────────────────
  if (step === 'otp') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="verification-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#667eea" opacity="0.1"/>
                <rect x="16" y="22" width="32" height="22" rx="3" stroke="#667eea" strokeWidth="2.5" fill="none"/>
                <path d="M16 25l16 12 16-12" stroke="#667eea" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <h1>Enter Reset Code</h1>
            <p>We sent a 6-digit code to</p>
            <p style={{ fontWeight: 600, color: '#2d3748', fontSize: '16px', marginTop: '4px' }}>
              {email}
            </p>
          </div>

          <div className="otp-input-group" onPaste={handleOtpPaste}>
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => otpRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="otp-input"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                autoComplete="one-time-code"
                disabled={isLoading}
              />
            ))}
          </div>

          {error && <p className="otp-error-message">{error}</p>}

          <button
            className="auth-button"
            onClick={() => handleVerifyOtp()}
            disabled={isLoading || otp.some(d => d === '')}
            style={{ marginTop: '16px' }}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Verifying…
              </>
            ) : (
              'Verify Code'
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <p style={{ color: '#a0aec0', fontSize: '13px', margin: '0 0 12px' }}>
              Didn't receive the code? Check your spam folder.
            </p>
            <button
              className="auth-button auth-button-outline"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              style={{ padding: '8px 24px', fontSize: '13px', minHeight: 'auto' }}
            >
              {resending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Code'}
            </button>
            {resendMessage && (
              <p style={{ color: '#38a169', fontSize: '14px', marginTop: '8px' }}>
                {resendMessage}
              </p>
            )}
          </div>

          <div className="auth-footer" style={{ marginTop: '16px' }}>
            <p style={{ color: '#94a3b8', fontSize: '12px' }}>
              Code expires in 10 minutes
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── RENDER: Email input (Step 1) ──────────────────────────────
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/newlogo.png" alt={PLATFORM_NAME} className="auth-brand-logo" />
          <h1>Forgot Password</h1>
          <p>Enter your email and we'll send you a reset code</p>
        </div>

        <form onSubmit={handleSendOtp} className="auth-form">
          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="currentColor"/>
              </svg>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              placeholder="Enter your registered email"
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Sending…
              </>
            ) : (
              'Send Reset Code'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <Link to="/login">Login here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
